import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FaPlus } from "react-icons/fa"
import { FiSettings, FiTrash2 } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { ConfigContainer, Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import {
  limsProductTestPlan,
  limsForskrifter,
  analysisDefinitions,
  sampleView,
  message,
} from "state_management"

// Product test plan curation (F-B2). QC defines an authoritative set of analyses (and thus
// their F-B1 specs) per product, independent of sample history. The backend's auto-tracked
// product→analysis relationships (sampleView.productAnalysis) are shown as *suggestions*.
//
// The master list is the union of products that have a curated plan and products seen on
// samples. The editor lets QC pick the curated analysis set (with spec ✓/⚠ status), promote
// suggestions into the plan, and clean stray auto-tracked relationships. The whole LimsScreen
// is already gated behind showLims, so no extra flag check is needed here.
export default function ProductTestPlan() {
  const { t } = useT("common")
  const dispatch = useDispatch()

  // null = closed; { productName: "", isNew: true } = adding; { productName, isNew: false } = editing.
  const [editing, setEditing] = useState(null)
  // Product name pending a delete confirmation (the row-level "delete the whole plan" action).
  const [confirmDeleteName, setConfirmDeleteName] = useState(null)

  const plannedNames = useSelector(limsProductTestPlan.selectPlannedProductNames)
  const productAnalysis = useSelector((state) => state.sampleView.productAnalysis)
  const plans = useSelector((state) => state.limsProductTestPlan)
  const specsMap = useSelector((state) => state.limsSpecs)
  // Effective method documents (forskrifter) and per-definition review specs feed the three
  // readiness columns below; read the whole slices and resolve per-definition via their selectors.
  const forskrifter = useSelector(limsForskrifter.selectDocuments)
  const reviewSpecMap = useSelector((state) => state.limsReviewSpec)

  useEffect(() => {
    dispatch(limsProductTestPlan.fetch())
    // SPEC-021 (B2): the QC + review specs ride on the analysis-definition payload — one
    // definitions fetch hydrates both spec slices (the spec ✓/⚠ and reviewer/duration columns).
    dispatch(analysisDefinitions.fetch())
    // Forskrifter (effective method docs) back the docs column.
    dispatch(limsForskrifter.fetch())
    // Ensure the auto-tracked product→analysis relationships are loaded. The sample-view
    // fetch needs no caller params (it computes its own window), so it is safe to dispatch
    // when the data is missing.
    if (!productAnalysis) {
      dispatch(sampleView.fetch())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Master list: union of curated product names and productAnalysis keys, sorted. Empty /
  // null product keys (samples with no product name) are skipped — they cannot be curated.
  const products = useMemo(() => {
    const names = new Set()
    plannedNames.forEach((n) => names.add(n))
    Object.keys(productAnalysis || {}).forEach((n) => {
      if (n && n !== "null" && n !== "undefined") names.add(n)
    })
    return [...names].sort((a, b) => a.localeCompare(b))
  }, [plannedNames, productAnalysis])

  // Set of analysis-definition ids that have an EFFECTIVE method document linked to them. Built
  // once from the forskrifter slice so the per-product readiness pass is a cheap membership test
  // instead of calling selectEffectiveForDefinition per (product, definition) pair.
  const defsWithEffectiveDoc = useMemo(() => {
    const out = new Set()
    const docs = forskrifter || []
    docs.forEach((doc) => {
      const hasEffective = (doc.versions || []).some(
        (v) => v.state === limsForskrifter.FORSKRIFT_STATES.EFFECTIVE
      )
      if (hasEffective) {
        const defIds = doc.analysisDefinitionIds || []
        defIds.forEach((defId) => out.add(defId))
      }
    })
    return out
  }, [forskrifter])

  // Readiness per product across its curated analyses: how many have a usable F-B1 spec, a
  // calculated parameter on that spec, an effective method document, a reviewer competence group,
  // and a review duration set. Computed from the slice maps directly so we don't call the combined
  // selectors per row.
  const readiness = useMemo(() => {
    const out = {}
    products.forEach((name) => {
      const ids = (plans[name] && plans[name].analysisDefinitionIds) || []
      const withSpec = ids.filter((defId) => specHasContent(specsMap[defId])).length
      const withCalc = ids.filter((defId) =>
        (specsMap[defId]?.parameters || []).some((p) => p && p.kind === "calculated")
      ).length
      const withDoc = ids.filter((defId) => defsWithEffectiveDoc.has(defId)).length
      const withReviewerGroup = ids.filter(
        (defId) => (reviewSpecMap[defId]?.allowedGroupIds?.length || 0) > 0
      ).length
      const withReviewDuration = ids.filter(
        (defId) => reviewSpecMap[defId]?.duration != null
      ).length
      out[name] = {
        total: ids.length,
        withSpec,
        withCalc,
        withDoc,
        withReviewerGroup,
        withReviewDuration,
      }
    })
    return out
  }, [products, plans, specsMap, defsWithEffectiveDoc, reviewSpecMap])

  const handleClose = () => setEditing(null)

  // Delete a product from the table with everything under it — its curated plan (if any) AND
  // its auto-tracked product→analysis relationships — so the row disappears. Confirmed first.
  const handleDelete = (productName) => {
    const ops = []
    if (plannedNames.includes(productName)) {
      ops.push(dispatch(limsProductTestPlan.removeForProduct(productName)))
    }
    const relationships = (productAnalysis && productAnalysis[productName]) || []
    relationships.forEach((rel) => {
      if (rel && rel.id) ops.push(dispatch(sampleView.removeProductAnalysis(rel.id)))
    })
    Promise.all(ops)
      .then(() => dispatch(message.success(t("limsProductPlanDeleted", { name: productName }))))
      .catch(() => dispatch(message.error(t("limsProductPlanDeleteFailed", { name: productName }))))
    setConfirmDeleteName(null)
  }

  return (
    <div className="LimsProducts">
      <div className="LimsProducts-header">
        <h1 className="LimsProducts-title">{t("limsProductReadiness")}</h1>
        <Button variant="primary" onClick={() => setEditing({ productName: "", isNew: true })}>
          <FaPlus /> {t("limsProductAdd")}
        </Button>
      </div>

      {products.length === 0 ? (
        <p className="LimsProducts-empty">{t("limsProductsNoEntries")}</p>
      ) : (
        <Table striped hover responsive className="LimsProducts-table">
          <thead>
            <tr>
              <th>{t("limsProductName")}</th>
              <th>{t("limsProductCuratedCount")}</th>
              <th>{t("limsProductSpecReadiness")}</th>
              <th>{t("limsProductFormulaReadiness")}</th>
              <th>{t("limsProductDocsReadiness")}</th>
              <th>{t("limsProductReviewerGroupReadiness")}</th>
              <th>{t("limsProductReviewDurationReadiness")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {products.map((name) => {
              const hasPlan = plannedNames.includes(name)
              const r = readiness[name] || {
                total: 0,
                withSpec: 0,
                withCalc: 0,
                withDoc: 0,
                withReviewerGroup: 0,
                withReviewDuration: 0,
              }
              const showCounts = hasPlan && r.total > 0
              return (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{hasPlan ? r.total : t("limsProductNoPlan")}</td>
                  <td>
                    {showCounts
                      ? t("limsProductSpecReady", { withSpec: r.withSpec, total: r.total })
                      : "—"}
                  </td>
                  <td>
                    {showCounts
                      ? t("limsProductFormulaReady", { ready: r.withCalc, total: r.total })
                      : "—"}
                  </td>
                  <td>
                    {showCounts
                      ? t("limsProductDocsReady", { ready: r.withDoc, total: r.total })
                      : "—"}
                  </td>
                  <td>
                    {showCounts
                      ? t("limsProductReviewerGroupReady", {
                          ready: r.withReviewerGroup,
                          total: r.total,
                        })
                      : "—"}
                  </td>
                  <td>
                    {showCounts
                      ? t("limsProductReviewDurationReady", {
                          ready: r.withReviewDuration,
                          total: r.total,
                        })
                      : "—"}
                  </td>
                  <td className="LimsProducts-actions">
                    <div className="LimsProducts-actions-inner">
                      <FiSettings
                        className="LimsProducts-icon"
                        title={t("edit")}
                        onClick={() => setEditing({ productName: name, isNew: false })}
                      />
                      <FiTrash2
                        className="LimsProducts-icon LimsProducts-icon--danger"
                        title={t("delete")}
                        onClick={() => setConfirmDeleteName(name)}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {editing && (
        <ProductPlanEditor
          productName={editing.productName}
          isNew={editing.isNew}
          existingNames={products}
          onClose={handleClose}
        />
      )}

      {confirmDeleteName && (
        <Popup nonScrollable onCancel={() => setConfirmDeleteName(null)}>
          <div className="LimsProducts-confirm-dialog">
            <p>{t("limsProductConfirmDelete", { name: confirmDeleteName })}</p>
            <div className="LimsProducts-confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmDeleteName(null)}>
                {t("cancel")}
              </Button>
              <Button variant="danger" onClick={() => handleDelete(confirmDeleteName)}>
                {t("delete")}
              </Button>
            </div>
          </div>
        </Popup>
      )}
    </div>
  )
}

// Whether the spec stored for a definition id has usable QC content (≥1 parameter or a
// formula). Mirrors the same predicate in the limsProductTestPlan slice.
function specHasContent(spec) {
  if (!spec) return false
  const hasParams = Array.isArray(spec.parameters) && spec.parameters.length > 0
  const hasFormula = typeof spec.formula === "string" && spec.formula.trim() !== ""
  return hasParams || hasFormula
}

// Editor popup: pick the curated analysis set (multi-select with spec status) and reconcile
// suggestions. Mirrors ConsumablesRegister's in-file ConfigContainer + Popup pattern.
function ProductPlanEditor({ productName: initialName, isNew, existingNames, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const [productName, setProductName] = useState(initialName || "")
  const [reason, setReason] = useState("")

  // The combined view for the current product name. For a brand-new product the name may be
  // empty until typed; selectProductTestPlan returns an empty curated/empty-or-historic
  // suggestions set in that case.
  const combined = useSelector((state) =>
    limsProductTestPlan.selectProductTestPlan(state, productName)
  )
  const definitions = useSelector((state) => state.analysisDefinitions)
  const specsMap = useSelector((state) => state.limsSpecs)
  const plannedNames = useSelector(limsProductTestPlan.selectPlannedProductNames)

  // Local working copy of the curated analysis-definition ids; seed from the stored plan.
  const [selectedIds, setSelectedIds] = useState(() => combined.curated.map((c) => c.analysisDefinitionId))

  // The analysis-definition collection for the multi-select ({ id, name }).
  const definitionCollection = useMemo(
    () =>
      Object.values(definitions || {})
        .filter((d) => d && d.name)
        .map((d) => ({ id: d.id, name: d.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [definitions]
  )

  const isSpecDefined = (defId) => specHasContent(specsMap[defId])

  const handleSubmit = () => {
    const trimmed = (productName || "").trim()
    if (!trimmed) {
      dispatch(message.error(t("limsProductNameRequired")))
      return false
    }
    // Removing every analysis and saving deletes the plan record — the master table has no
    // delete action, so the editor is the single place to manage the plan and its analyses.
    if (selectedIds.length === 0) {
      if (plannedNames.includes(trimmed)) {
        dispatch(limsProductTestPlan.removeForProduct(trimmed))
          .then(() => {
            dispatch(message.success(t("limsProductPlanDeleted", { name: trimmed })))
            onClose()
          })
          .catch(() => {
            dispatch(message.error(t("limsProductPlanDeleteFailed", { name: trimmed })))
          })
      } else {
        onClose()
      }
      return true
    }
    const wasNew = !existingNames.includes(trimmed)
    dispatch(limsProductTestPlan.setForProduct(trimmed, selectedIds, reason.trim() || null, wasNew))
      .then(() => {
        dispatch(message.success(t("limsProductPlanSaved", { name: trimmed })))
        onClose()
      })
      .catch(() => {
        dispatch(message.error(t("limsProductPlanSaveFailed", { name: trimmed })))
      })
    return true
  }

  return (
    <Popup nonScrollable onCancel={onClose}>
      <ConfigContainer
        title={isNew ? t("limsProductAdd") : t("limsProductEdit", { name: initialName })}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
        className="LimsProducts-editor"
      >
        {isNew ? (
          <Container className="LimsProducts-field">
            <Title>
              {t("limsProductName")} <div className="red-text">*</div>
            </Title>
            <Text
              value={productName}
              required
              onChange={setProductName}
              placeholder={t("limsProductNamePlaceholder")}
            />
          </Container>
        ) : (
          <h3 className="LimsProducts-product-title">{productName}</h3>
        )}

        <div className="LimsProducts-field">
          <h4 className="LimsProducts-section-heading">{t("limsProductCuratedPlan")}</h4>
          <Select
            isMulti
            collection={definitionCollection}
            value={selectedIds}
            onChange={(values) => setSelectedIds(values || [])}
            menuPlacement="bottom"
          />
          {selectedIds.length > 0 && (
            <ul className="LimsProducts-spec-list">
              {selectedIds.map((defId) => {
                const def = definitions[defId]
                const defined = isSpecDefined(defId)
                return (
                  <li key={defId} className="LimsProducts-spec-item">
                    <span className="LimsProducts-spec-name">{def ? def.name : defId}</span>
                    <span className="LimsProducts-spec-right">
                      <span className={"LimsProducts-spec-badge " + (defined ? "is-defined" : "is-missing")}>
                        {defined ? t("limsProductSpecDefined") : t("limsProductSpecMissing")}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <Container className="LimsProducts-field">
          <Title>{t("limsAuditReason")}</Title>
          <Text
            value={reason}
            onChange={setReason}
            placeholder={t("limsProductReasonPlaceholder")}
          />
        </Container>
      </ConfigContainer>
    </Popup>
  )
}
